import * as widgetActions from '../../../actions/widget.actions';

import {
  Input,
  inject,
  Output,
  Component,
  DestroyRef,
  EventEmitter,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { select, Store } from '@ngrx/store';
import { faGear } from '@fortawesome/free-solid-svg-icons';
import { TranslateService } from '@ngx-translate/core';

import { LoggerService } from '@services/logger.service';
import { readableTextColor } from 'src/app/shared/utils/color-utils';
import { ObjectEditorService } from 'src/app/core/services/object-editor.service';
import { ObjectActionsService } from 'src/app/core/services/object-actions.service';

import {
  Language,
  ObjectDefinition,
  Widget,
} from 'src/app/core/models/core.models';
import {
  WidgetType,
  NomSupportedLanguages,
  PopupType,
  NomObjectTypes,
  NomObjectClass,
} from 'src/app/core/constants';

import {
  deleteWidget,
  updateWidget,
  widgetMassOperation,
} from 'src/app/core/actions/widget.actions';

import {
  selectCurrentObject,
  selectSelectedObjects,
  selectWidgetsLanguageId,
} from 'src/app/core/reducers';
import { AppState } from 'src/app/reducers';

import {
  tap,
  map,
  filter,
  Subject,
  Observable,
  debounceTime,
  distinctUntilChanged,
  take,
  switchMap,
} from 'rxjs';
import {
  DialogCloseOptions,
  DialogCloseReason,
  DialogService,
} from '@sui/dialog';
import { ConfirmDialogComponent } from '../../popups/confirm-dialog/confirm-dialog.component';
import { WidgetOperation } from 'src/app/core/models/widget-operations';
import { MoveCopyWidgetDialogComponent } from 'src/app/shared/components/move-copy-widget-dialog/move-copy-widget-dialog.component';
import { PickerDataobjectsDialogComponent } from '../../popups/picker-dataobjects-dialog/picker-dataobjects-dialog.component';

@Component({
  selector: 'app-widget-card-list',
  templateUrl: './widget-card-list.component.html',
  styleUrls: ['./widget-card-list.component.scss'],
  standalone: false,
})
export class WidgetCardListComponent {
  @Input() widget: Widget;
  @Input() objectActionService: ObjectActionsService;
  @Input() searchString: string;
  @Output() widgetSelected = new EventEmitter<boolean>();
  @Output() selectSectionBreak = new EventEmitter<Widget>();

  isSelected$: Observable<boolean>;
  currentLanguageId: number;
  currentLanguage: string;
  widgetTitle: string;
  exportWidgetTitle: string = '';

  protected destroyRef = inject(DestroyRef);
  protected translateService = inject(TranslateService);
  protected logger = inject(LoggerService);
  protected dialogService = inject(DialogService);
  widgetTypes = WidgetType;

  faGear = faGear;

  get headerColor(): string | null {
    return this.widget?.settings?.widget_header_color || null;
  }

  // * readable text color for the row when a custom background color is set
  get headerTextColor(): string | null {
    return this.headerColor ? readableTextColor(this.headerColor) : null;
  }

  constructor(
    private store: Store<AppState>,
    private editService: ObjectEditorService,
  ) {}

  ngOnInit() {
    if (this.widget) {
      this.widgetTitle = this.widget?.title;

      this.exportWidgetTitle =
        this.widget?.settings?.export_title?.[
          'language_id_' + this.currentLanguageId
        ] ?? '';
    }

    this.store
      .select(selectWidgetsLanguageId)
      .pipe(
        filter((id): id is number => id != null),
        distinctUntilChanged(),
        tap((id) => {
          const lang = NomSupportedLanguages.find((l) => l.id === id);

          this.currentLanguageId = id;
          this.currentLanguage = lang ? lang.name : '';

          this.exportWidgetTitle =
            this.widget?.settings?.export_title?.['language_id_' + id] ?? '';
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.isSelected$ = this.store.select(selectSelectedObjects).pipe(
      takeUntilDestroyed(this.destroyRef),
      map((selectedObjects) => selectedObjects.has(this.widget.id)),
      distinctUntilChanged(),
    );
  }

  onTitleBlur() {
    const updated = { ...this.widget, title: this.widgetTitle };
    console.log(updated.title);

    this.store.dispatch(
      updateWidget({ widgetId: this.widget.id, data: updated }),
    );
  }

  onExportTitleBlur() {
    const widgetCopy = {
      ...this.widget,
      settings: {
        ...this.widget.settings,
        export_title: {
          ...this.widget.settings.export_title,
          ['language_id_' + this.currentLanguageId]: this.exportWidgetTitle,
        },
      },
    };

    this.store.dispatch(
      updateWidget({ widgetId: this.widget.id, data: widgetCopy }),
    );
  }

  editWidget(widget, tab?: string) {
    this.editService.editWidget(widget, tab);
  }

  selectSectionHeading(widget) {
    this.objectActionService.toggleObjectSelection(widget);
    this.selectSectionBreak.emit(widget);
  }

  updatePageBreak(currentPageBreak: boolean) {
    const widgetCopy = {
      ...this.widget,
      settings: {
        ...this.widget.settings,
        section_break_page_break_before: !currentPageBreak,
      },
    };

    this.store.dispatch(
      updateWidget({ widgetId: this.widget.id, data: widgetCopy }),
    );
  }
  private buildObjectMap(objects?: any[]): Map<string, ObjectDefinition> {
    return new Map(
      objects?.map((object) => [
        object.id,
        {
          object_class_id: object.object_class_id,
          object_type_id: object.object_type_id,
          object_id: object.id,
        },
      ]) || [],
    );
  }

  configurePopup(
    objectTypeIds: NomObjectTypes[],
    selectedObjectIds: Map<string, ObjectDefinition>,
    onResolvedAction: (objectIds: string[]) => any,
    popupType: PopupType,
  ) {
    const dialogRef = this.dialogService.openTabsDialog(
      PickerDataobjectsDialogComponent,
      {
        data: { objectTypeIds, selectedObjectIds, popupType },
        height: '70vh',
        width: '61vw',
        minWidth: 1220,
        maxWidth: 1550,
        closeOnClick: false,
        closeOnEscape: true,
      },
    );

    dialogRef.closed.pipe(take(1)).subscribe((event: any) => {
      if (
        event?.reason === DialogCloseReason.Save ||
        event?.reason === DialogCloseReason.SaveAndClose
      ) {
        this.store.dispatch(onResolvedAction(event?.data));
      }

      console.log(
        '%c Dialog closed with event:',
        'color: green; font-weight: bold;',
        event,
      );
    });
  }

  configureDatasource() {
    this.configurePopup(
      [
        NomObjectTypes.ONLINE_FEED,
        NomObjectTypes.STORY_FEED,
        NomObjectTypes.SOCIAL_FEED,
        NomObjectTypes.CLIPPING_FEED,
        NomObjectTypes.BROADCAST_FEED,
        NomObjectTypes.COMBINED_FEED,
      ],
      this.buildObjectMap(this.widget.datasources),
      (objectIds) =>
        widgetActions.applyWidgetDatasourceIds({
          widgetId: this.widget.id,
          datasourceIds: objectIds,
          datasourceClassIds: Array(objectIds?.length).fill(
            NomObjectClass.FEED,
          ),
        }),
      PopupType.SELECT_DATASOURCES,
    );
  }

  configureFilters() {
    this.configurePopup(
      [NomObjectTypes.SEARCH_DIMENSION],
      this.buildObjectMap(this.widget.dimensions_filter),
      (objectIds) =>
        widgetActions.applyWidgetFilterIds({
          widgetId: this.widget.id,
          filterIds: objectIds,
        }),
      PopupType.ATTACH_DIMENSIONS_AS_FILTERS,
    );
  }

  configurePivots() {
    this.configurePopup(
      [NomObjectTypes.SEARCH_DIMENSION],
      this.buildObjectMap(this.widget.dimensions_pivot),
      (objectIds) =>
        widgetActions.applyWidgetPivotIds({
          widgetId: this.widget.id,
          pivotIds: objectIds,
        }),
      PopupType.ATTACH_DIMENSIONS_AS_PIVOTS,
    );
  }

  duplicateWidget() {
    this.store
      .pipe(
        select(selectCurrentObject),
        take(1),
        filter(Boolean),
        switchMap((currentObject) => {
          const dialogRef = this.dialogService.openSimpleDialog(
            MoveCopyWidgetDialogComponent,
            {
              data: {
                widgets: [this.widget],
                currentObject,
              },
              closeOnClick: false,
            },
          );

          return dialogRef.closed.pipe(take(1));
        }),
        filter(
          (result: DialogCloseOptions<{ data: WidgetOperation[] }>) =>
            result?.reason === DialogCloseReason.Save && !!result?.data?.data,
        ),
        tap((result) => {
          this.store.dispatch(
            widgetMassOperation({
              operations: result.data.data,
            }),
          );
        }),
      )
      .subscribe();
  }

  deleteWidget() {
    const message = this.translateService.instant(
      'widget-card.delete-widget.conformation-are-you-sure-you-want-to-delete-this-widget',
    );

    const dialogRef = this.dialogService.openSimpleDialog(
      ConfirmDialogComponent,
      {
        data: {
          message,
        },
        closeOnClick: false,
      },
    );

    dialogRef.closed.subscribe(
      (event: DialogCloseOptions<{ reason: DialogCloseReason }>) => {
        if (event.reason === DialogCloseReason.Confirm) {
          this.store.dispatch(deleteWidget({ widgetId: this.widget.id }));
        } else if (event.reason === DialogCloseReason.Cancel) {
          this.logger.debug(`Deletion cancelled: ${event.reason}`);
        }
      },
    );
  }
}
